import { memo, useCallback, useMemo } from 'react';

import { type BaseCustomField, type CustomFieldValue, type ResponseCustomFieldValue } from '@air/api/types';
import { useBatchUpdateFormAssetsMutation } from '@air/feature-content-collection/hooks';
import {
  type ContentCollectionCustomField,
  contentCollectionSessionIdSelector,
} from '@air/feature-content-collection/redux';

import {
  type ContentCollectionBaseCustomFieldsProps,
  ContentCollectionCustomFieldsSelect,
} from '~/components/ContentCollection/ContentCollectionBaseCustomFieldsSelect';
import { useAirStore } from '~/utils/ReduxUtils';

export type ContentCollectionCustomFieldMultiSelectProps = {
  customField: BaseCustomField;
  onSuccess: (params: {
    customFieldWithValues: ContentCollectionCustomField;
    values: CustomFieldValue[] | null;
  }) => void;
  values: ResponseCustomFieldValue[] | null | undefined;
} & Omit<ContentCollectionBaseCustomFieldsProps, 'field'>;

export const ContentCollectionCustomFieldMultiSelect = memo(
  ({
    clips,
    customField,
    disabled,
    onSuccess,
    values,
    showDescription,
  }: ContentCollectionCustomFieldMultiSelectProps) => {
    const store = useAirStore();
    const { mutate: batchUpdateFormAssets, isPending } = useBatchUpdateFormAssetsMutation();

    const customFieldWithValues = useMemo<ContentCollectionCustomField>(
      () => ({
        ...customField,
        value: null,
        values: values ?? null,
      }),
      [customField, values],
    );

    const onSelectionChange = useCallback(
      ({ allValueIds, values }: { allValueIds?: string[]; values: CustomFieldValue[] | null }) => {
        const sessionId = contentCollectionSessionIdSelector(store.getState());

        if (!sessionId) {
          throw new Error('No session id');
        }

        /**
         * @TODO - Hacky this is a workaround to override the existing custom field values. Since the BE doesn't allow us to just pass in `valueIds`
         */
        batchUpdateFormAssets(
          {
            assetIds: clips.map(({ assetId }) => assetId),
            formSessionId: sessionId,
            update: {
              customFields: [
                {
                  customFieldId: customField.id,
                  valueIdsToRemove: allValueIds,
                },
              ],
            },
          },
          {
            onSuccess: () => {
              batchUpdateFormAssets(
                {
                  assetIds: clips.map(({ assetId }) => assetId),
                  formSessionId: sessionId,
                  update: {
                    customFields: [
                      {
                        customFieldId: customField.id,
                        valueIdsToAdd: values?.map(({ id }) => id),
                      },
                    ],
                  },
                },
                {
                  onSuccess: () => {
                    onSuccess({ customFieldWithValues, values });
                  },
                },
              );
            },
          },
        );
      },
      [batchUpdateFormAssets, clips, customField.id, customFieldWithValues, onSuccess, store],
    );

    return (
      <ContentCollectionCustomFieldsSelect
        disabled={disabled || isPending}
        clips={clips}
        field={customFieldWithValues}
        onSelectionChange={onSelectionChange}
        showDescription={showDescription}
      />
    );
  },
);

ContentCollectionCustomFieldMultiSelect.displayName = 'ContentCollectionCustomFieldMultiSelect';
